// AI Build Lab — Student Innovation & Entrepreneurship Camp
// Redesigned Feb 2026 for the K-12 audience of parents, principals, teachers,
// after-school directors, community partners, and sponsors. Brand palette:
// Navy #082B5C · Royal #1268E8 · Orange #FF7A00 · White · Light-blue #EAF4FF.
// Do NOT restyle to match the adult donjawilliams.com — this experience is
// intentionally distinct.
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowRight, Sparkles, Users, Target, Lightbulb, Rocket, Presentation,
  CheckCircle2, Trophy, Send, Loader2, Compass, Brain, LayoutDashboard,
  MessageSquare, TrendingUp, Wrench, Search, Menu, X,
  School, Home, Handshake, HeartHandshake, GraduationCap, Award,
  ChevronDown, DollarSign, ShieldCheck, MapPin, ClipboardCheck,
  Briefcase, FileCheck2, BookOpen, Building2, Scale, Shield,
} from "lucide-react";
import { toast } from "sonner";
import { submitOrgInquiry } from "@/lib/api";
import { useSiteContent } from "@/lib/siteContent";

// ---------- BRAND SYSTEM ----------
const C = {
  navy: "#082B5C",
  royal: "#1268E8",
  orange: "#FF7A00",
  white: "#FFFFFF",
  lightBlue: "#EAF4FF",
  softNeutral: "#F5F7FA",
  text: "#152238",
  muted: "#5A6B85",
};

const HERO_IMG = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=2400&q=85";
const SECONDARY_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_future-builders-33/artifacts/8pauhhq9_image.png";

const JOURNEY = [
  { n: "01", label: "Discover a problem", icon: Search },
  { n: "02", label: "Understand the user", icon: Users },
  { n: "03", label: "Develop an idea", icon: Lightbulb },
  { n: "04", label: "Build with AI", icon: Brain },
  { n: "05", label: "Create an app or website", icon: LayoutDashboard },
  { n: "06", label: "Develop the business", icon: TrendingUp },
  { n: "07", label: "Pitch the solution", icon: Presentation },
];

const OUTCOMES = [
  { title: "Functional app or website prototype", body: "A working digital product students can demonstrate and continue developing after the program." },
  { title: "Clearly defined real-world problem", body: "Students learn to identify challenges that genuinely matter — in their school, family, or community." },
  { title: "Target user and customer insight", body: "Simple, structured customer discovery so students design solutions people actually need." },
  { title: "Product name and brand identity", body: "The basics of positioning: a clear name, a simple identity, and a message that resonates." },
  { title: "Business or social-impact concept", body: "Whether it becomes a business, nonprofit, or school initiative — students learn how the model works." },
  { title: "Marketing and funding strategy", body: "A straightforward launch plan and either a startup budget or a funding concept." },
  { title: "Three-minute business pitch", body: "Students practice explaining their idea with confidence to real audiences and mentors." },
  { title: "Portfolio-ready project", body: "A digital artifact students can share on college applications, interviews, and student portfolios." },
];

const PROBLEM_EXAMPLES = [
  "Academic organization", "Student wellness", "Peer tutoring", "School communication",
  "Financial literacy", "Community resources", "Environmental challenges", "Volunteer opportunities",
  "College & career readiness", "Small business tools",
];

// ---- Middle School track ----
const MS_EMPHASIS = [
  "AI literacy", "Problem solving", "Design thinking", "Research",
  "Entrepreneurship", "Responsible AI", "Communication", "Collaboration",
  "Early workforce exposure",
];

// ---- High School track ----
const HS_EXPERIENCE = [
  "Real employer engagement",
  "Workplace expectations",
  "Deadlines and deliverables",
  "Stakeholder and market research",
  "Requirements development",
  "AI workflow and architecture",
  "Prompt engineering and automation",
  "User acceptance testing",
  "Data-driven iteration",
  "Client feedback",
  "AI governance",
  "Privacy and security",
  "Bias and responsible AI",
  "Intellectual-property considerations",
  "Professional communication",
  "Teamwork",
  "Professional presentations",
  "Career pathway exposure",
];

const HS_PORTFOLIO = [
  "Problem statement",
  "Stakeholder and market research",
  "Solution requirements",
  "AI workflow and architecture",
  "Functional MVP / prototype",
  "Testing results",
  "Change log",
  "Responsible-AI and risk assessment",
  "Executive summary",
  "Final client presentation",
  "Portfolio case study",
  "Defense of key technical, business, and ethical decisions",
];

const WORKFORCE_HS = [
  "Meeting deadlines",
  "Understanding requirements",
  "Solving ambiguous problems",
  "Working collaboratively",
  "Receiving feedback",
  "Revising work",
  "Communicating with stakeholders",
  "Documenting decisions",
  "Presenting professionally",
  "Defending recommendations with evidence",
];

export default function StudentCamp() {
  const sc = useSiteContent();
  const [inquiryType, setInquiryType] = useState("school");
  const setRoleAndScroll = (role) => {
    setInquiryType(role);
    setTimeout(() => {
      const el = document.getElementById("inquiry");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  // ---- SEO: hydrate document metadata for /student-camp ----
  useEffect(() => {
    const title = (sc.sc_meta_title || "").trim() ||
      "AI Build Lab | Student Innovation and Entrepreneurship Camp";
    const desc = (sc.sc_meta_description || "").trim() ||
      "The AI Build Lab is a six-week, in-person after-school innovation and entrepreneurship camp where middle and high school students use AI to solve real-world problems and build an app or website.";
    const canonical = "https://donjawilliams.com/student-camp";
    const prevTitle = document.title;
    document.title = title;

    const upsertMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      return el;
    };
    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
      return el;
    };

    upsertMeta("name", "description", desc);
    upsertMeta("name", "keywords",
      "AI Build Lab, Student Innovation and Entrepreneurship Camp, after-school AI program for middle school students, after-school AI program for high school students, student AI camp, AI entrepreneurship program for students, student app development program, student website development camp, STEAM after-school program, entrepreneurship camp for middle and high school students, AI education program for schools, Charlotte after-school technology program, Charlotte student entrepreneurship program"
    );
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:site_name", "Freedom In Me");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);
    upsertLink("canonical", canonical);

    // JSON-LD structured data — Course + Organization
    const existing = document.getElementById("sc-jsonld");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "sc-jsonld";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Course",
          "name": sc.sc_program_name_full || "AI Build Lab: Student Innovation and Entrepreneurship Camp",
          "description": desc,
          "provider": {
            "@type": "Organization",
            "name": "Freedom In Me",
            "url": "https://donjawilliams.com",
          },
          "url": canonical,
          "educationalLevel": "Middle school, High school (Grades 6–12)",
          "courseMode": "in-person",
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "in-person",
            "courseWorkload": "PT12H",
          },
          "audience": {
            "@type": "EducationalAudience",
            "educationalRole": "student",
          },
        },
        {
          "@type": "Organization",
          "name": "Freedom In Me",
          "url": "https://donjawilliams.com",
          "sameAs": ["https://donjawilliams.com/student-camp"],
        },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      const s = document.getElementById("sc-jsonld");
      if (s) s.remove();
    };
  }, [sc.sc_meta_title, sc.sc_meta_description, sc.sc_program_name_full]);

  return (
    <div className="min-h-screen bg-white" data-testid="student-camp-page" style={{ color: C.text, fontFamily: "'Inter', -apple-system, 'Segoe UI', system-ui, sans-serif" }}>
      <Nav />
      <main>
        <Hero sc={sc} />
        <ValueBar />
        <FutureRequires />
        <WhatIsIt />
        <IdeaToImpact />
        <TwoTracks sc={sc} />
        <MiddleSchoolTrack />
        <WhatStudentsBuild />
        <HighSchoolTrack sc={sc} />
        <HighSchoolPortfolio sc={sc} />
        <WorkforceReadiness />
        <StandardsAlignment sc={sc} />
        <RealProblems />
        <Showcase />
        <EnrollmentModels sc={sc} onParentCTA={() => setRoleAndScroll("parent")} onSchoolCTA={() => setRoleAndScroll("school")} onSponsorCTA={() => setRoleAndScroll("sponsor")} />
        <AboutFreedomInMe />
        <FAQ />
        <FinalCTA />
        <InquiryForm inquiryType={inquiryType} setInquiryType={setInquiryType} />
      </main>
      <Foot />
    </div>
  );
}

/* ---------- NAV ---------- */
function Nav() {
  const [open, setOpen] = useState(false);
  const items = [
    { href: "#tracks", label: "Ages 6–12" },
    { href: "#experience", label: "The Experience" },
    { href: "#outcomes", label: "Student Outcomes" },
    { href: "#enroll", label: "Enroll" },
    { href: "#faq", label: "FAQ" },
    { href: "#inquiry", label: "Contact" },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b" style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(20px)", borderColor: "rgba(8,43,92,0.08)" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/student-camp" data-testid="sc-logo" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl grid place-items-center text-white shadow-md" style={{ background: `linear-gradient(135deg, ${C.royal}, ${C.navy})` }}>
            <span className="font-black text-[15px] tracking-tight">AI</span>
          </div>
          <div>
            <div className="font-black text-[14px] tracking-tight leading-none" style={{ color: C.navy }}>AI BUILD LAB</div>
            <div className="text-[10px] tracking-[0.18em] uppercase font-semibold leading-none mt-0.5" style={{ color: C.orange }}>Student Innovation</div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-[14px] font-medium" style={{ color: C.text }}>
          {items.map((l) => (
            <a key={l.href} href={l.href} data-testid={`sc-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="hover:opacity-70 transition-opacity">{l.label}</a>
          ))}
        </nav>
        <a href="#inquiry" className="hidden md:block" data-testid="sc-nav-cta">
          <Button className="rounded-full h-10 px-5 font-bold text-[12px] tracking-[0.05em] uppercase text-white hover:opacity-90 shadow-md" style={{ background: C.orange }}>
            Bring the Lab to your school
          </Button>
        </a>
        <button onClick={() => setOpen(!open)} className="md:hidden p-2" data-testid="sc-mobile-toggle" aria-label="Menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t bg-white px-6 py-5 flex flex-col gap-4" style={{ borderColor: "rgba(8,43,92,0.08)" }}>
          {items.map((l) => <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-base font-medium">{l.label}</a>)}
          <a href="#inquiry" onClick={() => setOpen(false)}>
            <Button className="w-full h-12 rounded-full text-white font-bold uppercase tracking-[0.05em] text-[13px]" style={{ background: C.orange }}>Bring the Lab to your school</Button>
          </a>
        </div>
      )}
    </header>
  );
}

/* ---------- HERO — full-width cinematic photograph ---------- */
const HERO_STATS = [
  { icon: Rocket,          label: "Six-week program" },
  { icon: MapPin,          label: "In-person, after-school" },
  { icon: GraduationCap,   label: "Grades 6–12" },
  { icon: LayoutDashboard, label: "App or website prototype" },
  { icon: Trophy,          label: "Innovation Showcase" },
];

function Hero({ sc }) {
  const programFull = (sc?.sc_program_name_full || "").trim() ||
    "AI Build Lab: Student Innovation and Entrepreneurship Camp";
  return (
    <section
      className="relative overflow-hidden isolate min-h-[92vh] lg:min-h-[100vh] flex flex-col"
      data-testid="sc-hero"
    >
      {/* --- FULL-WIDTH PHOTOGRAPH --- */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMG}
          alt="Diverse middle and high school students collaborating on laptops in a modern innovation lab"
          className="w-full h-full object-cover object-[65%_center]"
          fetchPriority="high"
          data-testid="sc-hero-image"
        />
      </div>

      {/* --- BLUE GRADIENT OVERLAY (navy left → clear right) --- */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: `linear-gradient(90deg,
            ${C.navy} 0%,
            ${C.navy}F0 22%,
            ${C.navy}B0 45%,
            ${C.navy}55 68%,
            transparent 100%)`,
        }}
      />
      {/* Mobile / tablet: darker bottom for legibility */}
      <div
        className="absolute inset-0 lg:hidden -z-10"
        style={{
          background: `linear-gradient(180deg, ${C.navy}CC 0%, ${C.navy}80 45%, ${C.navy}E6 100%)`,
        }}
      />
      {/* Bottom navy fade for the info bar */}
      <div
        className="absolute inset-x-0 bottom-0 h-56 -z-10 pointer-events-none"
        style={{ background: `linear-gradient(180deg, transparent 0%, ${C.navy}D9 100%)` }}
      />

      {/* --- Subtle orange brand accent (top-left glow) --- */}
      <div
        className="absolute top-1/3 -left-24 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none -z-10"
        style={{ background: C.orange }}
      />

      {/* --- Content wrapper: pushes headline block to top-middle, stats to bottom --- */}
      <div className="relative flex-1 flex flex-col max-w-[1240px] w-full mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-10">
        <div className="flex-1 flex items-center">
          <div className="max-w-[820px]">
            {/* Program tag */}
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-7 border backdrop-blur-md"
              style={{ background: "rgba(255,255,255,0.10)", borderColor: "rgba(255,255,255,0.22)" }}
              data-testid="sc-hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.orange }} />
              <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.22em] text-white/95">
                Six-week · In-person · After-school · Grades 6–12
              </span>
            </div>

            {/* Headline — bold sans-serif, no script */}
            <h1
              className="font-black uppercase leading-[0.92] tracking-[-0.025em] text-white"
              style={{ fontSize: "clamp(38px, 6.6vw, 88px)" }}
              data-testid="sc-hero-headline"
            >
              Preparing students to{" "}
              <span style={{ color: C.orange }}>build with AI</span>
              <span className="text-white">&mdash;not just use it.</span>
            </h1>

            {/* Full program name — SEO-critical + brand consistency */}
            <p
              className="mt-5 text-[13px] sm:text-[14px] font-bold uppercase tracking-[0.18em] text-white/85"
              data-testid="sc-hero-program-name"
            >
              {programFull}
            </p>

            {/* Supporting copy */}
            <p
              className="mt-5 max-w-[620px] text-[16px] sm:text-[18px] leading-[1.55] text-white/85"
              data-testid="sc-hero-subhead"
            >
              A six-week, in-person after-school innovation and entrepreneurship experience where middle and high school students use AI to solve a real-world problem and develop an app or website solution.
            </p>

            {/* CTAs */}
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href="#inquiry" data-testid="sc-hero-primary">
                <Button
                  className="h-14 px-7 rounded-full text-white font-bold uppercase text-[12px] sm:text-[13px] hover:opacity-95 transition-opacity"
                  style={{
                    background: C.orange,
                    letterSpacing: "0.06em",
                    boxShadow: `0 18px 40px -12px ${C.orange}CC, 0 4px 8px rgba(0,0,0,0.25)`,
                  }}
                >
                  Bring the AI Build Lab to your school
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </a>
              <a href="#inquiry" data-testid="sc-hero-secondary">
                <Button
                  variant="outline"
                  className="h-14 px-6 rounded-full font-bold uppercase text-[12px] sm:text-[13px] bg-transparent text-white hover:bg-white/10 border-2"
                  style={{ borderColor: "rgba(255,255,255,0.55)", letterSpacing: "0.06em" }}
                >
                  Schedule a Conversation
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom info bar — clean, translucent, no tilted stickers */}
        <div className="mt-12 lg:mt-20" data-testid="sc-hero-stats">
          <div
            className="rounded-2xl backdrop-blur-md border py-4 sm:py-5 px-4 sm:px-6 lg:px-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5"
            style={{
              background: "rgba(8,43,92,0.55)",
              borderColor: "rgba(255,255,255,0.15)",
              boxShadow: "0 20px 50px -20px rgba(0,0,0,0.4)",
            }}
          >
            {HERO_STATS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.label}
                  className={`flex items-center gap-3 ${i > 2 ? "col-span-1 sm:col-span-1" : ""}`}
                  data-testid={`sc-hero-stat-${i}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0 border"
                    style={{ background: "rgba(255,255,255,0.08)", borderColor: "rgba(255,255,255,0.2)" }}
                  >
                    <Icon size={16} className="text-white" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: C.orange }}>
                      {i === 0 ? "Duration" : i === 1 ? "Format" : i === 2 ? "Audience" : i === 3 ? "Deliverable" : "Finale"}
                    </div>
                    <div className="text-white text-[13px] sm:text-[14px] font-bold leading-tight mt-0.5 truncate">
                      {s.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- VALUE BAR ---------- */
function ValueBar() {
  const items = [
    { k: "AI Literacy", v: "Responsible" }, { k: "Skills Taught", v: "12+" },
    { k: "Final Deliverable", v: "Live Pitch" }, { k: "Audience", v: "Community" },
  ];
  return (
    <section className="border-y" style={{ background: C.navy, borderColor: "rgba(255,255,255,0.06)" }} data-testid="sc-value-bar">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((i) => (
          <div key={i.k} className="text-center md:text-left">
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-1.5" style={{ color: C.orange }}>{i.k}</div>
            <div className="font-black text-[22px] text-white leading-tight">{i.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- FUTURE REQUIRES ---------- */
function FutureRequires() {
  return (
    <section className="py-20 lg:py-28" data-testid="sc-future" style={{ background: C.softNeutral }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>The AI Reality</div>
          <h2 className="font-black leading-[1.05] tracking-[-0.025em]" style={{ fontSize: "clamp(30px, 4vw, 52px)", color: C.navy }}>
            The future requires <span style={{ color: C.royal }}>more than</span> AI access.
          </h2>
          <p className="mt-6 text-[17px] leading-[1.7]" style={{ color: C.muted }}>
            Students already use AI every day. What they need now is the guidance to use it responsibly, think critically, solve real problems, create meaningful solutions, and communicate their ideas with confidence.
          </p>
          <p className="mt-4 text-[17px] leading-[1.7] font-semibold" style={{ color: C.text }}>
            The AI Build Lab turns AI from a tool students consume into a tool they build with.
          </p>
        </div>
        <div className="lg:col-span-6 grid grid-cols-2 gap-4">
          {[
            { icon: Brain, label: "Responsible AI use", tone: "navy" },
            { icon: Target, label: "Critical thinking", tone: "royal" },
            { icon: Wrench, label: "Real solutions built", tone: "orange" },
            { icon: MessageSquare, label: "Confident communication", tone: "navy" },
          ].map((c) => {
            const Icon = c.icon;
            const bg = c.tone === "orange" ? C.orange : c.tone === "royal" ? C.royal : C.navy;
            return (
              <div key={c.label} className="rounded-2xl p-5 sm:p-6 text-white" style={{ background: bg }}>
                <Icon size={24} className="mb-4 opacity-90" />
                <div className="font-bold text-[15px] leading-tight">{c.label}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- WHAT IS IT ---------- */
function WhatIsIt() {
  return (
    <section id="experience" className="py-24 lg:py-32" data-testid="sc-what-is-it">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>The Program</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(34px, 5vw, 64px)", color: C.navy }}>
            What is the <span style={{ color: C.royal }}>AI Build Lab?</span>
          </h2>
          <p className="mt-6 text-[19px] leading-[1.65]" style={{ color: C.muted }}>
            A six-week experience that helps students move from technology consumers to <strong style={{ color: C.text }}>creators, entrepreneurs, and problem-solvers.</strong> Students identify a challenge that matters to them and use AI and no-code technology to develop an app or website solution.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
          {[
            { n: "Real-world", label: "problem-solving", body: "Students choose a challenge in their school, family, or community. No fake case studies." },
            { n: "AI-powered", label: "creation", body: "Age-appropriate AI and no-code tools guide students from idea to functional prototype." },
            { n: "Entrepreneurship", label: "at the core", body: "Every project also becomes a business or impact concept — pitched to real audiences." },
          ].map((c, i) => (
            <div key={i} className="rounded-2xl p-7 border transition-transform hover:-translate-y-1" style={{ background: C.white, borderColor: "rgba(8,43,92,0.08)" }} data-testid={`sc-whatis-card-${i}`}>
              <div className="text-[11px] uppercase font-bold tracking-[0.18em]" style={{ color: C.orange }}>{c.n}</div>
              <div className="font-black text-[22px] leading-tight mt-1" style={{ color: C.navy }}>{c.label}</div>
              <p className="mt-3 text-[15px] leading-[1.65]" style={{ color: C.muted }}>{c.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- IDEA TO IMPACT (visual pathway) ---------- */
function IdeaToImpact() {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" style={{ background: C.navy }} data-testid="sc-idea-to-impact">
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-15 blur-3xl" style={{ background: C.royal }} />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl" style={{ background: C.orange }} />
      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>The Student Journey</div>
          <h2 className="font-black text-white leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(34px, 5vw, 64px)" }}>
            From an idea <span style={{ color: C.orange }}>→</span> to impact.
          </h2>
          <p className="mt-6 text-[18px] leading-[1.65] text-white/75 max-w-2xl">
            The seven-step pathway every student walks — turning a real challenge into a functional prototype they can demonstrate, defend, and continue developing.
          </p>
        </div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-8 left-8 right-8 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${C.orange}, ${C.royal}, transparent)` }} />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 lg:gap-3">
            {JOURNEY.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="relative flex flex-col items-center text-center" data-testid={`sc-journey-${i}`}>
                  <div className="w-16 h-16 rounded-2xl grid place-items-center relative z-10 shadow-lg mb-4" style={{ background: i === JOURNEY.length - 1 ? C.orange : C.white, color: i === JOURNEY.length - 1 ? C.white : C.navy }}>
                    <Icon size={26} strokeWidth={2.2} />
                  </div>
                  <div className="text-[10px] font-bold tracking-[0.18em] mb-1" style={{ color: C.orange }}>STEP {s.n}</div>
                  <div className="font-bold text-white text-[13px] leading-tight px-1">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- WHAT STUDENTS BUILD ---------- */
function WhatStudentsBuild() {
  return (
    <section id="outcomes" className="py-24 lg:py-32" data-testid="sc-outcomes" style={{ background: C.white }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>By the End of Six Weeks</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(34px, 5vw, 64px)", color: C.navy }}>
            What Middle School students <span style={{ color: C.royal }}>walk away with.</span>
          </h2>
          <p className="mt-6 text-[18px] leading-[1.65]" style={{ color: C.muted }}>
            Not a certificate. Not just theory. Every student leaves with a <strong style={{ color: C.text }}>functional prototype</strong> and the vocabulary to sell it — from problem statement to pitch.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {OUTCOMES.map((o, i) => (
            <div key={i} className="rounded-2xl p-6 border h-full transition-all hover:shadow-lg hover:-translate-y-0.5" style={{ background: C.softNeutral, borderColor: "rgba(8,43,92,0.06)" }} data-testid={`sc-outcome-${i}`}>
              <div className="w-9 h-9 rounded-lg grid place-items-center mb-4 font-black text-white text-[13px]" style={{ background: i % 3 === 0 ? C.orange : i % 3 === 1 ? C.royal : C.navy }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="font-bold text-[16px] leading-tight" style={{ color: C.navy }}>{o.title}</div>
              <p className="mt-2 text-[14px] leading-[1.6]" style={{ color: C.muted }}>{o.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 max-w-3xl rounded-2xl p-5 border-l-4" style={{ background: C.lightBlue, borderColor: C.royal }}>
          <p className="text-[14px] leading-[1.65] italic" style={{ color: C.navy }}>
            <strong>Note on outcomes:</strong> the AI Build Lab is intentionally scoped to a <strong>functional prototype</strong>, not a fully launched commercial product. That keeps six weeks achievable — while still giving each student something they can demonstrate, defend, and continue developing after the program.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- TWO TRACKS · One Program. Two Age-Appropriate Experiences. ---------- */
function TwoTracks({ sc = {} }) {
  const eyebrow = (sc.sc_tracks_eyebrow || "").trim() || "One Program · Two age-appropriate experiences";
  const headlineHtml = (sc.sc_tracks_headline_html || "").trim() || "Same program. <em>Two ways</em> students experience it.";
  const intro = (sc.sc_tracks_intro || "").trim() || "The AI Build Lab is one cohesive Grades 6–12 program with two age-appropriate paths — so what a 6th grader walks away with is meaningfully different from what a 12th grader walks away with.";
  const msTitle = (sc.sc_ms_card_title || "").trim() || "Student Innovation Lab";
  const msTagline = (sc.sc_ms_card_tagline || "").trim() || "Discover it · Design it · Build it · Test it · Present it";
  const msBody = (sc.sc_ms_card_body || "").trim() || "Middle school students explore real-world challenges and use AI to develop apps, websites, or digital solutions. They learn how to understand users, research problems, develop ideas, build and test solutions, think responsibly about AI, explore entrepreneurship, and present their work to an authentic audience.";
  const hsTitle = (sc.sc_hs_card_title || "").trim() || "Industry Challenge Edition";
  const hsTagline = (sc.sc_hs_card_tagline || "").trim() || "Real company · Real problem · Real deliverables · Real feedback · Real portfolio";
  const hsBody = (sc.sc_hs_card_body || "").trim() || "High school students take on a real industry-sponsored workplace challenge. They analyze the problem, research stakeholders and the market, establish requirements and success criteria, design an AI-enabled solution, build a functional MVP, conduct structured testing, analyze risk, iterate based on evidence and feedback, and pitch the solution directly to the partner.";
  const hsQuote = (sc.sc_hs_positioning_quote || "").trim() || "Don't just prepare students to enter the workforce. Give them an opportunity to practice being in it.";

  return (
    <section id="tracks" className="py-24 lg:py-32" data-testid="sc-two-tracks" style={{ background: C.softNeutral }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>{eyebrow}</div>
          <h2
            className="font-black leading-[1.02] tracking-[-0.03em] [&_em]:not-italic"
            style={{ fontSize: "clamp(34px, 5vw, 64px)", color: C.navy }}
            dangerouslySetInnerHTML={{ __html: headlineHtml.replace(/<em>/g, `<em style="color:${C.royal};font-style:normal">`) }}
          />
          <p className="mt-6 text-[18px] leading-[1.65]" style={{ color: C.muted }}>
            {intro}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
          {/* --- Middle School card --- */}
          <div
            className="rounded-3xl p-7 sm:p-9 bg-white border-2 relative overflow-hidden flex flex-col"
            style={{ borderColor: "rgba(8,43,92,0.08)" }}
            data-testid="sc-track-middle-card"
          >
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.orange }} />
            <div className="flex items-center gap-2 mb-4">
              <div className="w-11 h-11 rounded-xl grid place-items-center shadow-md" style={{ background: C.orange }}>
                <Lightbulb size={22} className="text-white" strokeWidth={2.2} />
              </div>
              <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: C.orange }}>
                Middle School · Grades 6–8
              </div>
            </div>
            <h3 className="font-black text-[26px] sm:text-[30px] leading-[1.05] tracking-[-0.02em]" style={{ color: C.navy }}>
              {msTitle}
            </h3>
            <p className="mt-2 text-[13px] font-bold uppercase tracking-[0.14em]" style={{ color: C.royal }}>
              {msTagline}
            </p>
            <p className="mt-5 text-[15px] leading-[1.65]" style={{ color: C.text }}>
              {msBody}
            </p>
            <div className="mt-5 rounded-xl p-4 border-l-4" style={{ background: C.softNeutral, borderLeftColor: C.orange }}>
              <p className="text-[13.5px] leading-[1.6]" style={{ color: C.text }}>
                Middle school students may <strong>identify a challenge that matters to them</strong> or work from a <strong>real-world challenge provided by a school, nonprofit, community organization, or business partner</strong>.
              </p>
            </div>
            <div className="mt-auto pt-6">
              <a href="#middle-school" className="inline-flex items-center gap-2 font-bold text-[13px] uppercase tracking-[0.06em]" style={{ color: C.orange }} data-testid="sc-track-middle-cta">
                See what Middle School students do <ArrowRight size={14} />
              </a>
            </div>
          </div>

          {/* --- High School card --- */}
          <div
            className="rounded-3xl p-7 sm:p-9 border-2 relative overflow-hidden flex flex-col"
            style={{ background: C.navy, borderColor: "rgba(255,255,255,0.1)" }}
            data-testid="sc-track-high-card"
          >
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.orange }} />
            <div className="flex items-center gap-2 mb-4">
              <div className="w-11 h-11 rounded-xl grid place-items-center shadow-md" style={{ background: C.orange }}>
                <Briefcase size={22} className="text-white" strokeWidth={2.2} />
              </div>
              <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: C.orange }}>
                High School · Grades 9–12
              </div>
            </div>
            <h3 className="font-black text-[26px] sm:text-[30px] leading-[1.05] tracking-[-0.02em] text-white">
              {hsTitle}
            </h3>
            <p className="mt-2 text-[13px] font-bold uppercase tracking-[0.14em]" style={{ color: C.orange }}>
              {hsTagline}
            </p>
            <p className="mt-5 text-[15px] leading-[1.65] text-white/85">
              {hsBody}
            </p>
            <div className="mt-5 rounded-xl p-4 border-l-4" style={{ background: "rgba(255,255,255,0.06)", borderLeftColor: C.orange }}>
              <p className="text-[14px] leading-[1.55] italic text-white">
                &ldquo;{hsQuote}&rdquo;
              </p>
            </div>
            <div className="mt-auto pt-6">
              <a href="#high-school" className="inline-flex items-center gap-2 font-bold text-[13px] uppercase tracking-[0.06em]" style={{ color: C.orange }} data-testid="sc-track-high-cta">
                See what High School students do <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- MIDDLE SCHOOL TRACK · deep-dive ---------- */
function MiddleSchoolTrack() {
  return (
    <section id="middle-school" className="py-24 lg:py-32" data-testid="sc-middle-school" style={{ background: C.white }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Middle School · Grades 6–8</div>
            <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", color: C.navy }}>
              &ldquo;I can use AI to <span style={{ color: C.royal }}>identify problems and build solutions.</span>&rdquo;
            </h2>
            <p className="mt-6 text-[17px] leading-[1.65]" style={{ color: C.muted }}>
              Middle school students don&rsquo;t simply build technology. From week one they begin thinking like founders: <strong style={{ color: C.text }}>Who would use this? What problem does it solve? Why would someone use it? Is it a business, nonprofit, school initiative, or community-impact solution? How would people learn about it? How would I explain and pitch it?</strong>
            </p>
            <p className="mt-4 text-[15px] leading-[1.65]" style={{ color: C.muted }}>
              The result: a functional prototype, a clearly-defined user, a product name and brand identity, a business or social-impact concept, a simple marketing or funding idea, and a three-minute pitch practiced with real audiences.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl p-7 border-2" style={{ background: C.softNeutral, borderColor: "rgba(8,43,92,0.08)" }}>
              <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.navy }}>Emphasis at this age</div>
              <div className="flex flex-wrap gap-2.5" data-testid="sc-ms-emphasis">
                {MS_EMPHASIS.map((chip, i) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full font-semibold text-[13px]"
                    style={{
                      background: C.white,
                      color: C.navy,
                      border: `1.5px solid ${i % 3 === 0 ? C.orange : i % 3 === 1 ? C.royal : "rgba(8,43,92,0.15)"}`,
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: i % 3 === 0 ? C.orange : i % 3 === 1 ? C.royal : C.navy }} />
                    {chip}
                  </span>
                ))}
              </div>
              <div className="mt-6 rounded-xl p-4 border-l-4" style={{ background: C.white, borderLeftColor: C.orange }}>
                <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-1.5" style={{ color: C.orange }}>Early workforce exposure</div>
                <p className="text-[13.5px] leading-[1.6]" style={{ color: C.text }}>
                  For younger students we frame professional skills as <strong>exposure, not performance</strong> — meeting deadlines, receiving feedback, revising work, presenting to adults, and defending their thinking are treated as first-time practice, not workforce-level expectations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- HIGH SCHOOL TRACK · Industry Challenge Edition ---------- */
function HighSchoolTrack({ sc = {} }) {
  const quote = (sc.sc_hs_positioning_quote || "").trim() || "Don't just prepare students to enter the workforce. Give them an opportunity to practice being in it.";
  const body = (sc.sc_hs_card_body || "").trim() || "High school students take on a real industry-sponsored workplace challenge. They analyze the problem, research stakeholders and the market, establish requirements and success criteria, design an AI-enabled solution, build a functional MVP, conduct structured testing, analyze risk, iterate based on evidence and feedback, and pitch the solution directly to the partner.";
  return (
    <section id="high-school" className="py-24 lg:py-32 relative overflow-hidden" data-testid="sc-high-school" style={{ background: C.navy, color: "white" }}>
      <div className="absolute top-20 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: C.orange }} />
      <div className="absolute bottom-20 -left-24 w-96 h-96 rounded-full opacity-15 blur-3xl pointer-events-none" style={{ background: C.royal }} />

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>High School · Grades 9–12 · Industry Challenge Edition</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em] text-white" style={{ fontSize: "clamp(32px, 4.8vw, 60px)" }}>
            &ldquo;I can work with a real client, <span style={{ color: C.orange }}>build and test an AI-enabled solution,</span> respond to feedback, and communicate my work professionally.&rdquo;
          </h2>
          <p className="mt-6 text-[17px] leading-[1.65] text-white/85">
            {body}
          </p>
        </div>

        <div className="rounded-3xl p-7 sm:p-8 mb-10 relative overflow-hidden" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="sc-hs-quote">
          <div className="absolute top-4 left-4 text-[80px] leading-none font-black opacity-20" style={{ color: C.orange }}>&ldquo;</div>
          <div className="relative pl-14">
            <p className="text-[20px] sm:text-[24px] leading-[1.35] font-bold tracking-[-0.01em] text-white">
              &ldquo;{quote}&rdquo;
            </p>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-5" style={{ color: C.orange }}>What high school students experience</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sc-hs-experience">
            {HS_EXPERIENCE.map((item, i) => (
              <div
                key={item}
                className="rounded-xl px-4 py-3.5 flex items-start gap-3"
                style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
              >
                <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: C.orange }} />
                <span className="text-[14px] leading-[1.4] text-white/90">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- HIGH SCHOOL PORTFOLIO · career-ready deliverables ---------- */
function HighSchoolPortfolio({ sc = {} }) {
  const headlineHtml = (sc.sc_hs_portfolio_headline_html || "").trim() || "A career-ready portfolio &mdash; <em>not just an app.</em>";
  const body = (sc.sc_hs_portfolio_body || "").trim() || "High school students leave the Industry Challenge Edition with an <strong>industry-facing portfolio package</strong> documenting their full body of work &mdash; the artifact of a real project, not just a class assignment.";
  const quote = (sc.sc_hs_graduate_quote || "").trim() || "I worked on an industry-sponsored project for a real organization. Here was the problem, my role, the AI-enabled solution we built, how we tested and improved it, and what we learned.";
  return (
    <section className="py-24 lg:py-32" data-testid="sc-hs-portfolio" style={{ background: C.softNeutral }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>High School · Deliverables</div>
          <h2
            className="font-black leading-[1.02] tracking-[-0.03em] [&_em]:not-italic"
            style={{ fontSize: "clamp(32px, 4.8vw, 60px)", color: C.navy }}
            dangerouslySetInnerHTML={{ __html: headlineHtml.replace(/<em>/g, `<em style="color:${C.royal};font-style:normal">`) }}
          />
          <p className="mt-6 text-[17px] leading-[1.65]" style={{ color: C.muted }} dangerouslySetInnerHTML={{ __html: body }} />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sc-hs-portfolio-items">
          {HS_PORTFOLIO.map((item, i) => (
            <div
              key={item}
              className="rounded-2xl bg-white p-5 border h-full flex items-start gap-3.5 transition-all hover:shadow-lg hover:-translate-y-0.5"
              style={{ borderColor: "rgba(8,43,92,0.08)" }}
            >
              <div
                className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0 font-black text-white text-[12px]"
                style={{ background: i % 3 === 0 ? C.orange : i % 3 === 1 ? C.royal : C.navy }}
              >
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-[14.5px] font-bold leading-tight mt-1.5" style={{ color: C.navy }}>{item}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl p-6 sm:p-8 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.royal} 0%, ${C.navy} 100%)`, color: "white" }} data-testid="sc-hs-portfolio-quote">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: C.orange }}>What a graduate can say</div>
          <p className="text-[18px] sm:text-[20px] leading-[1.45] font-medium text-white">
            &ldquo;{quote}&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- WORKFORCE READINESS ---------- */
function WorkforceReadiness() {
  return (
    <section className="py-24 lg:py-32" data-testid="sc-workforce" style={{ background: C.white }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Workforce readiness</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(32px, 4.8vw, 60px)", color: C.navy }}>
            Practice being <span style={{ color: C.royal }}>in the workforce</span> &mdash; not just preparing for it.
          </h2>
          <p className="mt-6 text-[17px] leading-[1.65]" style={{ color: C.muted }}>
            AI Build Lab exposes students to how work actually happens. High school students practice these habits at a professional level. Middle school students meet them as <strong style={{ color: C.text }}>first-time exposure</strong> &mdash; not workforce-level performance.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* HS — professional level */}
          <div className="lg:col-span-7 rounded-3xl p-7 border-2" style={{ background: C.softNeutral, borderColor: "rgba(8,43,92,0.08)" }} data-testid="sc-workforce-hs">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: C.royal }}>
                <Briefcase size={18} className="text-white" strokeWidth={2.3} />
              </div>
              <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: C.royal }}>High School · Professional practice</div>
            </div>
            <p className="text-[14px] leading-[1.6] mb-5" style={{ color: C.muted }}>
              High schoolers meet real deadlines, real requirements, and real feedback loops the way an early-career professional would.
            </p>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {WORKFORCE_HS.map((item) => (
                <div key={item} className="flex items-start gap-2.5 text-[13.5px]" style={{ color: C.text }}>
                  <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: C.royal }} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* MS — early exposure */}
          <div className="lg:col-span-5 rounded-3xl p-7 border-2 flex flex-col" style={{ background: C.white, borderColor: C.orange }} data-testid="sc-workforce-ms">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: C.orange }}>
                <Lightbulb size={18} className="text-white" strokeWidth={2.3} />
              </div>
              <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: C.orange }}>Middle School · Early exposure</div>
            </div>
            <p className="text-[14px] leading-[1.65]" style={{ color: C.muted }}>
              Middle school students are introduced to what professional work feels like &mdash; deadlines, expectations, feedback, revision, and presenting to adults &mdash; without being held to workforce-level performance.
            </p>
            <p className="mt-4 text-[13.5px] leading-[1.6] italic" style={{ color: C.navy }}>
              This is where a 6<sup>th</sup> or 7<sup>th</sup> grader learns, for the first time, that <strong>meeting a deadline</strong>, <strong>listening to a stakeholder</strong>, and <strong>revising after feedback</strong> are skills worth practicing early.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- STANDARDS ALIGNMENT ---------- */
function StandardsAlignment({ sc = {} }) {
  const disclaimer = (sc.sc_standards_disclaimer || "").trim() || "14 hours of applied enrichment reinforcing skills reflected across applicable North Carolina Computer Science, STEM, ELA/Literacy, communication, and workforce-readiness standards. Freedom In Me does not award academic credit and does not represent that Lab sessions count toward a specific NC standard.";
  return (
    <section className="py-24 lg:py-32" data-testid="sc-standards" style={{ background: C.lightBlue }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-10">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.royal }}>Standards alignment</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(32px, 4.8vw, 60px)", color: C.navy }}>
            Designed to reinforce <span style={{ color: C.royal }}>what students are already learning.</span>
          </h2>
          <p className="mt-6 text-[16.5px] leading-[1.65]" style={{ color: C.text }}>
            <strong>AI Build Lab is an after-school enrichment experience.</strong> It does not replace classroom instruction and is not a credit-bearing course. Activities reinforce and provide applied practice in skills reflected across applicable North Carolina academic and workforce-readiness standards.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
          {/* MS standards */}
          <div className="rounded-3xl bg-white p-7 border-2 relative overflow-hidden" style={{ borderColor: "rgba(8,43,92,0.08)" }} data-testid="sc-standards-ms">
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.orange }} />
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Grades 6–8</div>
            <div className="flex flex-wrap gap-2">
              {["Computer Science", "STEM", "ELA / Literacy", "Speaking & Listening"].map((s) => (
                <span key={s} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full font-semibold text-[13.5px]" style={{ background: C.softNeutral, color: C.navy, border: "1.5px solid rgba(8,43,92,0.12)" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.orange }} />
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* HS standards */}
          <div className="rounded-3xl bg-white p-7 border-2 relative overflow-hidden" style={{ borderColor: "rgba(8,43,92,0.08)" }} data-testid="sc-standards-hs">
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.royal }} />
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.royal }}>Grades 9–12</div>
            <div className="flex flex-wrap gap-2">
              {["Computer Science", "STEM", "ELA / Literacy", "CTE / Workforce Readiness"].map((s) => (
                <span key={s} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full font-semibold text-[13.5px]" style={{ background: C.softNeutral, color: C.navy, border: "1.5px solid rgba(8,43,92,0.12)" }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.royal }} />
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-2xl p-5 sm:p-6 flex items-start gap-4 bg-white border-l-4" style={{ borderLeftColor: C.royal }} data-testid="sc-standards-note">
          <Scale size={22} style={{ color: C.royal }} className="flex-shrink-0 mt-0.5" />
          <p className="text-[14.5px] leading-[1.65]" style={{ color: C.text }}>
            {disclaimer}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- REAL PROBLEMS ---------- */
function RealProblems() {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" style={{ background: C.softNeutral }} data-testid="sc-problems">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-10">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Real problems. Student-built solutions.</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", color: C.navy }}>
            Where the <span style={{ color: C.royal }}>problem comes from</span> depends on the age.
          </h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 mb-10">
          <div className="lg:col-span-6 rounded-3xl bg-white p-7 border-2 relative overflow-hidden" style={{ borderColor: "rgba(8,43,92,0.08)" }} data-testid="sc-problems-ms">
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.orange }} />
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: C.orange }}>Middle School</div>
            <p className="text-[15px] leading-[1.65]" style={{ color: C.text }}>
              Middle school students may <strong>identify their own meaningful challenge</strong> &mdash; something they see in their school, family, or community &mdash; <strong>or work from a real-world challenge supplied by a school, nonprofit, community organization, or business partner.</strong>
            </p>
          </div>
          <div className="lg:col-span-6 rounded-3xl p-7 border-2 relative overflow-hidden" style={{ background: C.navy, borderColor: "rgba(255,255,255,0.1)" }} data-testid="sc-problems-hs">
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.orange }} />
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: C.orange }}>High School · Industry Challenge Edition</div>
            <p className="text-[15px] leading-[1.65] text-white/90">
              The Industry Challenge Edition centers on a <strong className="text-white">real challenge supplied or sponsored</strong> by a business, nonprofit, school, government or community organization, or other participating industry partner. Partners provide context and feedback but are <strong className="text-white">not obligated to implement</strong> a student solution.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: C.muted }}>Sample problem categories</div>
            <p className="text-[15px] leading-[1.65]" style={{ color: C.muted }}>
              These are the kinds of challenges middle school students often gravitate toward when they get to choose. High schoolers work with whatever the sponsoring partner brings.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-2.5" data-testid="sc-problem-chips">
              {PROBLEM_EXAMPLES.map((p, i) => (
                <span key={p} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-[14px] transition-all hover:-translate-y-0.5 hover:shadow-md cursor-default" style={{ background: C.white, color: C.navy, border: `1.5px solid ${i % 3 === 0 ? C.orange : i % 3 === 1 ? C.royal : "rgba(8,43,92,0.15)"}` }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: i % 3 === 0 ? C.orange : i % 3 === 1 ? C.royal : C.navy }} />
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- SHOWCASE ---------- */
function Showcase() {
  return (
    <section id="showcase" className="py-24 lg:py-32" data-testid="sc-showcase" style={{ background: C.white }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ boxShadow: `0 40px 80px -20px ${C.navy}40` }}>
              <img src={SECONDARY_IMG} alt="Students presenting their AI Build Lab prototype at the Innovation Showcase" className="w-full h-[440px] object-cover" />
            </div>
            <div className="absolute -bottom-5 -right-5 rounded-2xl px-5 py-4 shadow-xl" style={{ background: C.orange }}>
              <Trophy size={20} className="text-white mb-1" />
              <div className="text-[10px] uppercase font-bold tracking-widest text-white/90">Week 6 Finale</div>
              <div className="font-black text-white text-[16px] leading-none mt-1">Live Pitch Experience</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Student Innovation Showcase</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", color: C.navy }}>
            Students take the <span style={{ color: C.royal }}>stage.</span>
          </h2>
          <p className="mt-6 text-[17px] leading-[1.65]" style={{ color: C.muted }}>
            The program culminates in a live showcase where students present their work to families, educators, community leaders, mentors, and (for High School cohorts) the sponsoring industry partner. The format flexes to the age of the cohort.
          </p>

          <div className="mt-8 space-y-5">
            <div className="rounded-2xl p-5 border-l-4" style={{ background: C.softNeutral, borderLeftColor: C.orange }} data-testid="sc-showcase-ms">
              <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-2" style={{ color: C.orange }}>Middle School · Student pitch</div>
              <p className="text-[14.5px] leading-[1.65]" style={{ color: C.text }}>
                Middle school students present <strong>what they built, the problem it addresses, who it serves, how they developed and tested it,</strong> and its business or community-impact potential.
              </p>
            </div>

            <div className="rounded-2xl p-5 border-l-4" style={{ background: C.lightBlue, borderLeftColor: C.royal }} data-testid="sc-showcase-hs">
              <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-2" style={{ color: C.royal }}>High School · Client-style pitch and demo</div>
              <p className="text-[14.5px] leading-[1.65]" style={{ color: C.text }}>
                High school students deliver a more professional client-style pitch and demo &mdash; explaining and defending the <strong>problem, research, solution, MVP, testing results, business considerations, responsible-AI considerations,</strong> and their recommendations to the sponsoring partner.
              </p>
            </div>
          </div>

          <p className="mt-8 text-[13.5px] italic" style={{ color: C.muted }}>
            Families, educators, business leaders, sponsoring partners, and community members can be invited as audience members, mentors, or judges.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- PARTICIPATION MODEL · 3 pathways (Parents / Schools / Sponsors) ---------- */
function EnrollmentModels({ sc, onParentCTA, onSchoolCTA, onSponsorCTA }) {
  const cohortsOpen = (sc?.sc_cohorts_open || "").trim().toLowerCase() === "yes";
  const cohortsHtml = (sc?.sc_available_cohorts_html || "").trim();
  const stripeParentLink = (sc?.sc_stripe_parent_link || "").trim();

  const eyebrow = (sc?.sc_participation_eyebrow || "").trim() || "How the program comes to students";
  const headlineHtml = (sc?.sc_participation_headline_html || "").trim() ||
    "Flexible ways to bring the <em>Lab</em> to students.";
  const intro = (sc?.sc_participation_intro || "").trim() ||
    "The AI Build Lab can be offered as an approved after-school program where parents register and pay for their students individually. Schools and community partners may also choose to sponsor a complete cohort, subsidize registration, or fund student scholarships.";
  const noteHtml = (sc?.sc_participation_note_html || "").trim() ||
    "<p><strong>School approval</strong> creates the school-specific cohort. <strong>Parent registration</strong> secures each student's seat. School or sponsor funding may replace or reduce a parent payment — but never replaces the parent's registration and consent process.</p>";

  return (
    <section id="enroll" className="py-24 lg:py-32" data-testid="sc-enrollment-models" style={{ background: C.softNeutral }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        {/* Intro */}
        <div className="max-w-3xl mb-14">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }} data-testid="sc-enroll-eyebrow">
            {eyebrow}
          </div>
          <h2
            className="font-black leading-[1.02] tracking-[-0.03em] [&_em]:not-italic [&_em]:text-[color:inherit]"
            style={{ fontSize: "clamp(34px, 5vw, 62px)", color: C.navy }}
            data-testid="sc-enroll-headline"
            dangerouslySetInnerHTML={{
              __html: headlineHtml.replace(/<em>/g, `<em style="color:${C.royal};font-style:normal">`),
            }}
          />
          <p className="mt-6 text-[18px] leading-[1.65]" style={{ color: C.muted }} data-testid="sc-enroll-intro">
            {intro}
          </p>
        </div>

        {/* --- HOW IT WORKS · 3 steps · above the pathway cards --- */}
        <div className="mb-14" data-testid="sc-how-it-works">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-5 text-center sm:text-left" style={{ color: C.muted }}>
            How it works
          </div>
          <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-0">
            {[
              { n: "01", Icon: School,          title: "School approves",  hint: "Host + create the cohort" },
              { n: "02", Icon: ClipboardCheck,  title: "Parents register", hint: "Consent, info, and payment (if applicable)" },
              { n: "03", Icon: Rocket,          title: "Program begins",   hint: "Six weeks, in-person, after-school" },
            ].map((s, i, arr) => (
              <div key={s.n} className="relative flex flex-col items-center text-center sm:items-start sm:text-left sm:pr-6" data-testid={`sc-step-${i}`}>
                {/* Connector line — hidden on last step + mobile */}
                {i < arr.length - 1 && (
                  <div className="hidden sm:block absolute top-8 left-[calc(64px+8px)] right-0 h-px" style={{ background: `linear-gradient(90deg, ${C.royal}55, ${C.royal}22, transparent)` }} aria-hidden />
                )}
                <div className="flex items-center gap-3 mb-2 sm:mb-3">
                  <div
                    className="w-16 h-16 rounded-2xl grid place-items-center shadow-md relative"
                    style={{ background: C.white, border: `2px solid ${C.royal}` }}
                  >
                    <s.Icon size={26} strokeWidth={2.1} style={{ color: C.royal }} />
                    <span
                      className="absolute -top-2 -right-2 min-w-6 h-6 px-1.5 rounded-full grid place-items-center font-black text-[10px] text-white shadow"
                      style={{ background: C.orange }}
                    >
                      {s.n}
                    </span>
                  </div>
                </div>
                <h4 className="font-black text-[16px] sm:text-[17px] tracking-[-0.01em] leading-tight" style={{ color: C.navy }}>
                  {s.title}
                </h4>
                <p className="mt-1 text-[12.5px] leading-[1.5] max-w-[220px]" style={{ color: C.muted }}>
                  {s.hint}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Three pathways */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {/* --- For Parents --- */}
          <div
            className="rounded-3xl p-7 sm:p-8 border-2 relative overflow-hidden bg-white flex flex-col"
            style={{ borderColor: "rgba(8,43,92,0.08)" }}
            data-testid="sc-pathway-parents"
          >
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.orange }} />
            <div className="flex items-start gap-3.5 mb-5">
              <div className="w-12 h-12 rounded-xl grid place-items-center flex-shrink-0 shadow-md" style={{ background: C.orange }}>
                <Home size={22} className="text-white" strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: C.orange }}>For parents</div>
                <h3 className="font-black text-[22px] sm:text-[24px] leading-tight tracking-[-0.02em] mt-1" style={{ color: C.navy }}>
                  Enroll your student
                </h3>
              </div>
            </div>
            <p className="text-[14.5px] leading-[1.65] mb-5" style={{ color: C.muted }}>
              Once the AI Build Lab is approved at a participating school, parents can register and pay for their students through that school&rsquo;s secure registration portal. Each cohort is <strong>15–30 students</strong> (minimum 15, maximum 30).
            </p>

            {cohortsOpen && cohortsHtml ? (
              <div className="mb-5 rounded-xl p-4 border" style={{ background: C.lightBlue, borderColor: "rgba(8,43,92,0.08)" }} data-testid="sc-pathway-parents-cohorts-open">
                <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-2" style={{ color: C.navy }}>Participating schools</div>
                <div className="text-[13.5px] leading-[1.6]" style={{ color: C.text }} dangerouslySetInnerHTML={{ __html: cohortsHtml }} />
              </div>
            ) : (
              <div className="mb-5 rounded-xl p-4 border-l-4" style={{ background: "rgba(255,122,0,0.06)", borderLeftColor: C.orange }} data-testid="sc-pathway-parents-cohorts-closed">
                <div className="text-[13px] leading-[1.6]" style={{ color: C.text }}>
                  Parent registration will become available as school cohorts are approved.{" "}
                  <strong style={{ color: C.navy }}>Join the interest list to receive updates.</strong>
                </div>
              </div>
            )}

            <div className="mt-auto pt-2">
              {cohortsOpen && stripeParentLink ? (
                <a href={stripeParentLink} target="_blank" rel="noreferrer" data-testid="sc-pathway-parents-cta">
                  <span className="inline-flex items-center gap-2 h-12 px-5 rounded-full text-white font-bold uppercase text-[11.5px] tracking-[0.06em] shadow-md hover:opacity-95" style={{ background: C.orange, boxShadow: `0 12px 30px -8px ${C.orange}90` }}>
                    View Participating Schools <ArrowRight size={14} />
                  </span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={onParentCTA}
                  data-testid="sc-pathway-parents-cta"
                  className="inline-flex items-center gap-2 h-12 px-5 rounded-full font-bold uppercase text-[11.5px] tracking-[0.06em] text-white transition-opacity hover:opacity-95 shadow-md"
                  style={{ background: C.orange, boxShadow: `0 12px 30px -8px ${C.orange}90` }}
                >
                  View Participating Schools <ArrowRight size={14} />
                </button>
              )}
              <p className="mt-3 text-[11px] flex items-center gap-1.5" style={{ color: C.muted }}>
                <ShieldCheck size={11} />
                Registration completed by a parent or legal guardian.
              </p>
            </div>
          </div>

          {/* --- For Schools --- */}
          <div
            className="rounded-3xl p-7 sm:p-8 border-2 relative overflow-hidden bg-white flex flex-col"
            style={{ borderColor: "rgba(8,43,92,0.08)" }}
            data-testid="sc-pathway-schools"
          >
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.royal }} />
            <div className="flex items-start gap-3.5 mb-5">
              <div className="w-12 h-12 rounded-xl grid place-items-center flex-shrink-0 shadow-md" style={{ background: C.royal }}>
                <School size={22} className="text-white" strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: C.royal }}>For schools</div>
                <h3 className="font-black text-[22px] sm:text-[24px] leading-tight tracking-[-0.02em] mt-1" style={{ color: C.navy }}>
                  Host the program
                </h3>
              </div>
            </div>
            <p className="text-[14.5px] leading-[1.65] mb-5" style={{ color: C.muted }}>
              Bring the AI Build Lab to your school as a six-week, in-person after-school program. Cohorts run <strong>15–30 students</strong> and can be <strong>parent-paid, school-funded, sponsor-supported, scholarship-supported, subsidized, or funded through a combination of sources</strong>.
            </p>

            <ul className="space-y-2 mb-6">
              {[
                "Approval to offer the program",
                "Program space & school contact",
                "Help communicating with families",
                "Parent-paid, funded, subsidized, or combined",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[13.5px]" style={{ color: C.text }}>
                  <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: C.royal }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-2">
              <button
                type="button"
                onClick={onSchoolCTA}
                data-testid="sc-pathway-schools-cta"
                className="inline-flex items-center gap-2 h-12 px-5 rounded-full font-bold uppercase text-[11.5px] tracking-[0.06em] text-white transition-opacity hover:opacity-95 shadow-md"
                style={{ background: C.royal, boxShadow: `0 12px 30px -8px ${C.royal}90` }}
              >
                Bring the Lab to Your School <ArrowRight size={14} />
              </button>
              <p className="mt-3 text-[11px]" style={{ color: C.muted }}>
                Hosting does not require the school to fund the program.
              </p>
            </div>
          </div>

          {/* --- For Sponsors --- */}
          <div
            className="rounded-3xl p-7 sm:p-8 border-2 relative overflow-hidden flex flex-col"
            style={{ background: C.navy, borderColor: "rgba(255,255,255,0.1)" }}
            data-testid="sc-pathway-sponsors"
          >
            <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: C.orange }} />
            <div className="flex items-start gap-3.5 mb-5">
              <div className="w-12 h-12 rounded-xl grid place-items-center flex-shrink-0 shadow-md" style={{ background: C.orange }}>
                <HeartHandshake size={22} className="text-white" strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold tracking-[0.22em]" style={{ color: C.orange }}>For sponsors &amp; community partners</div>
                <h3 className="font-black text-[22px] sm:text-[24px] leading-tight tracking-[-0.02em] mt-1 text-white">
                  Expand student access
                </h3>
              </div>
            </div>
            <p className="text-[14.5px] leading-[1.65] mb-5 text-white/80">
              Expand student access by sponsoring individual seats, scholarships, or a complete school cohort.
            </p>

            <ul className="space-y-2 mb-6">
              {[
                "Sponsor a complete school cohort",
                "Fund a designated number of seats",
                "Underwrite scholarships",
                "Subsidize registration for every family",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[13.5px] text-white/90">
                  <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: C.orange }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-2">
              <button
                type="button"
                onClick={onSponsorCTA}
                data-testid="sc-pathway-sponsors-cta"
                className="inline-flex items-center gap-2 h-12 px-5 rounded-full font-bold uppercase text-[11.5px] tracking-[0.06em] text-white transition-opacity hover:opacity-95 shadow-md"
                style={{ background: C.orange, boxShadow: `0 12px 30px -8px ${C.orange}90` }}
              >
                Explore Sponsorship <ArrowRight size={14} />
              </button>
              <p className="mt-3 text-[11px] text-white/60">
                Sponsor a seat, a scholarship, or a full cohort.
              </p>
            </div>
          </div>
        </div>

        {/* Clarifying note */}
        <div
          className="mt-10 rounded-2xl p-5 sm:p-6 border-l-4"
          style={{ background: C.white, borderLeftColor: C.royal, boxShadow: "0 8px 24px -12px rgba(8,43,92,0.15)" }}
          data-testid="sc-enroll-clarifying-note"
        >
          <div
            className="prose-inline text-[14px] leading-[1.7] [&_strong]:font-bold"
            style={{ color: C.text }}
            dangerouslySetInnerHTML={{ __html: noteHtml }}
          />
        </div>
      </div>
    </section>
  );
}


/* ---------- ABOUT · WHO'S BEHIND THE PROGRAM ---------- */
function AboutFreedomInMe() {
  const sc = useSiteContent();
  const founderName = (sc.sc_founder_name || "").trim() || "Donja Williams";
  const founderTitle = (sc.sc_founder_title || "").trim() || "Founder and CEO, Freedom In Me, LLC";
  const photo = (sc.sc_founder_photo_url || "").trim();
  const bioHtml = (sc.sc_founder_bio_html || "").trim();
  const bridgeHtml = (sc.sc_workforce_bridge_html || "").trim();
  const orgHtml = (sc.sc_org_description_html || "").trim();
  const closing = (sc.sc_closing_statement || "").trim() ||
    "Freedom In Me helps employers prepare for the future of work — while helping students prepare to become its future workforce.";
  const deliveryNote = (sc.sc_program_delivery_note || "").trim();

  const expertise = [
    "Workforce development",
    "Business and entrepreneurship",
    "AI strategy and literacy",
    "Leadership and training",
    "Youth program development",
    "Future-ready skills",
  ];

  const initials = founderName.split(/\s+/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase() || "DW";

  return (
    <section id="about" className="py-24 lg:py-32 relative overflow-hidden" data-testid="sc-about" style={{ background: C.lightBlue }}>
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: C.royal }} />
      <div className="absolute bottom-0 -left-20 w-72 h-72 rounded-full opacity-10 blur-3xl" style={{ background: C.orange }} />

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        {/* --- Top: Founder card + headline + bio --- */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left column — founder portrait card */}
          <div className="lg:col-span-5">
            <div className="relative">
              <div
                className="rounded-[24px] overflow-hidden shadow-xl border-2 bg-white"
                style={{ borderColor: "rgba(8,43,92,0.08)" }}
                data-testid="sc-founder-card"
              >
                {photo ? (
                  <img
                    src={photo}
                    alt={`Portrait of ${founderName}, ${founderTitle}`}
                    className="w-full h-[440px] object-cover"
                    style={{ objectPosition: "center 22%" }}
                    data-testid="sc-founder-photo"
                  />
                ) : (
                  // Stylish monogram fallback when no photo URL is set — professional, not childish
                  <div
                    className="w-full h-[440px] grid place-items-center relative overflow-hidden"
                    style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.royal} 100%)` }}
                    data-testid="sc-founder-monogram"
                  >
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `radial-gradient(circle at 20% 20%, ${C.orange} 0%, transparent 40%), radial-gradient(circle at 80% 80%, ${C.royal} 0%, transparent 40%)` }} />
                    <div className="relative text-center">
                      <div className="text-white font-black tracking-[-0.06em] leading-none" style={{ fontSize: "clamp(88px, 12vw, 160px)" }}>
                        {initials}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full border" style={{ borderColor: "rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.08)", backdropFilter: "blur(6px)" }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: C.orange }} />
                        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/85">Portrait pending</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="p-6 sm:p-7">
                  <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-2" style={{ color: C.orange }}>Led by</div>
                  <h3 className="font-black text-[26px] leading-tight tracking-[-0.02em]" style={{ color: C.navy }} data-testid="sc-founder-name">{founderName}</h3>
                  <p className="text-[14px] mt-1" style={{ color: C.muted }} data-testid="sc-founder-title">{founderTitle}</p>
                </div>
              </div>

              <div className="hidden sm:block absolute -bottom-5 -right-5 rotate-[3deg] rounded-2xl px-4 py-3 shadow-xl" style={{ background: C.orange }}>
                <Award size={18} className="text-white mb-1" />
                <div className="text-[9px] uppercase font-bold tracking-widest text-white/90">Program by</div>
                <div className="font-black text-white text-[14px] leading-none mt-1">Freedom In Me, LLC</div>
                <div className="text-[10px] text-white/85 mt-1">Charlotte, NC</div>
              </div>
            </div>
          </div>

          {/* Right column — eyebrow, headline, bio */}
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Who&rsquo;s behind the program</div>
            <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(30px, 4.4vw, 54px)", color: C.navy }} data-testid="sc-about-headline">
              Bridging the gap between the <span style={{ color: C.royal }}>workforce employers need</span> and the students preparing to enter it.
            </h2>

            {bioHtml ? (
              <div
                className="prose-inline mt-7 space-y-4 max-w-none [&>p]:text-[16px] [&>p]:leading-[1.7]"
                style={{ color: C.text }}
                data-testid="sc-founder-bio"
                dangerouslySetInnerHTML={{ __html: bioHtml }}
              />
            ) : null}
          </div>
        </div>

        {/* --- Middle band: Workforce bridge --- */}
        {bridgeHtml && (
          <div className="mt-16 lg:mt-24 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: C.orange }}>Bridging education & the workforce</div>
              <div className="font-black leading-tight tracking-[-0.02em]" style={{ fontSize: "clamp(24px, 2.6vw, 34px)", color: C.navy }}>
                Two sides of the <span style={{ color: C.royal }}>workforce equation</span>.
              </div>
              <div className="mt-5 space-y-2.5">
                {[
                  "What employers and organizations need from the future workforce",
                  "What students need to learn before entering that workforce",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5 rounded-xl p-3 border" style={{ background: C.white, borderColor: "rgba(8,43,92,0.08)" }}>
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: C.orange }} />
                    <span className="text-[13.5px] leading-[1.55]" style={{ color: C.text }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="rounded-2xl p-6 lg:p-8 border-2" style={{ background: C.white, borderColor: "rgba(8,43,92,0.08)" }} data-testid="sc-workforce-bridge">
                <div
                  className="prose-inline space-y-4 [&>p]:text-[15.5px] [&>p]:leading-[1.7]"
                  style={{ color: C.text }}
                  dangerouslySetInnerHTML={{ __html: bridgeHtml }}
                />
              </div>
            </div>
          </div>
        )}

        {/* --- Expertise labels + Org description --- */}
        <div className="mt-16 lg:mt-24 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: C.orange }}>Areas of expertise</div>
            <h3 className="font-black leading-tight tracking-[-0.02em]" style={{ fontSize: "clamp(22px, 2.4vw, 30px)", color: C.navy }}>
              Where Donja&rsquo;s work has focused.
            </h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-2.5" data-testid="sc-expertise-labels">
              {expertise.map((label) => (
                <div
                  key={label}
                  className="rounded-xl px-4 py-3 border flex items-center gap-2.5"
                  style={{ background: C.white, borderColor: "rgba(8,43,92,0.1)" }}
                >
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: C.orange }} />
                  <span className="text-[13px] font-bold" style={{ color: C.navy }}>{label}</span>
                </div>
              ))}
            </div>

            {deliveryNote && (
              <div className="mt-6 rounded-xl p-4 border-l-4" style={{ background: "rgba(18,104,232,0.05)", borderLeftColor: C.royal }} data-testid="sc-delivery-note">
                <div className="text-[10px] uppercase font-bold tracking-[0.18em] mb-1" style={{ color: C.royal }}>Program delivery</div>
                <p className="text-[13px] leading-[1.65]" style={{ color: C.text }}>{deliveryNote}</p>
              </div>
            )}
          </div>

          <div className="lg:col-span-6">
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-3" style={{ color: C.orange }}>Program organization</div>
            <h3 className="font-black leading-tight tracking-[-0.02em]" style={{ fontSize: "clamp(22px, 2.4vw, 30px)", color: C.navy }}>
              Program by <span style={{ color: C.royal }}>Freedom In Me, LLC</span>.
            </h3>
            {orgHtml ? (
              <div
                className="prose-inline mt-5 space-y-3 [&>p]:text-[15px] [&>p]:leading-[1.7]"
                style={{ color: C.text }}
                data-testid="sc-org-description"
                dangerouslySetInnerHTML={{ __html: orgHtml }}
              />
            ) : null}
            <div className="mt-5 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 border" style={{ background: C.white, borderColor: "rgba(8,43,92,0.1)" }}>
              <MapPin size={13} style={{ color: C.royal }} />
              <span className="text-[12px] font-bold" style={{ color: C.navy }}>Charlotte, North Carolina</span>
            </div>
          </div>
        </div>

        {/* --- Closing takeaway --- */}
        <div
          className="mt-16 lg:mt-24 rounded-3xl p-8 sm:p-12 relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.royal} 100%)` }}
          data-testid="sc-closing-statement"
        >
          <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-15 blur-3xl" style={{ background: C.orange }} />
          <div className="relative max-w-4xl">
            <div className="text-[10px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>In one sentence</div>
            <p className="font-black leading-[1.15] tracking-[-0.02em] text-white" style={{ fontSize: "clamp(24px, 3.4vw, 44px)" }}>
              &ldquo;{closing}&rdquo;
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
const FAQ_ITEMS = [
  {
    q: "Who is the AI Build Lab designed for?",
    a: "Middle and high school students (grades 6–12). No prior coding or AI experience required. The program runs as two age-appropriate experiences within one cohesive program: the Student Innovation Lab for grades 6–8 and the Industry Challenge Edition for grades 9–12.",
  },
  {
    q: "What's the difference between the Middle School and High School experiences?",
    a: "Middle school students (grades 6–8) explore real-world challenges and use AI to build apps, websites, or digital solutions. They may identify their own challenge or work from a real-world problem supplied by a school, nonprofit, community organization, or business partner. The emphasis is AI literacy, design thinking, entrepreneurship, responsible AI, and early workforce exposure. High school students (grades 9–12) take on the Industry Challenge Edition — a real workplace challenge sponsored by an industry partner. They research stakeholders, define requirements, build a functional MVP, conduct structured testing, iterate on evidence and feedback, and pitch the solution directly to the sponsoring partner. High school students leave with an industry-facing portfolio, not just an app.",
  },
  {
    q: "How large is each cohort?",
    a: "Each cohort is 15–30 students (minimum 15, maximum 30). This range keeps every student engaged and gives instructors enough capacity for real 1:1 support during build, test, and pitch weeks.",
  },
  {
    q: "Is the AI Build Lab offered virtually or as a hybrid program?",
    a: "The AI Build Lab: Student Innovation and Entrepreneurship Camp is currently offered as a six-week, in-person after-school experience. In-person delivery allows students to collaborate, receive direct support, test their ideas, and prepare for the Student Innovation Showcase. Schools or organizations interested in a customized delivery model may contact us to discuss their needs, but virtual and hybrid options are not currently advertised as standard program offerings.",
  },
  {
    q: "Does the AI Build Lab replace classroom instruction or award academic credit?",
    a: "No. The AI Build Lab is an after-school enrichment experience. It does not replace classroom instruction and is not a credit-bearing course. Activities reinforce and provide applied practice in skills reflected across applicable North Carolina Computer Science, STEM, ELA/Literacy, communication, and (for grades 9–12) CTE/workforce-readiness standards. Freedom In Me does not represent that Lab sessions count toward a specific NC standard.",
  },
  {
    q: "Who pays for the AI Build Lab?",
    a: "The website's flexible funding model supports several options. Cohorts may be parent-paid, school-funded, sponsor-supported, scholarship-supported, subsidized, or funded through a combination of sources. Hosting the program does not require the school to pay for it. Parent registration and consent is still required regardless of how the seat is funded.",
  },
  {
    q: "Who completes a student's registration?",
    a: "A parent or legal guardian must complete the registration, consent, and emergency-contact information for each student — even when a school or sponsor is funding the seat. Students should not independently create a paid registration, agree to legal terms, submit payment, or provide medical or media-release consent through the website. School or sponsor funding may replace or reduce the parent payment, but never replaces the parent's registration and consent.",
  },
  {
    q: "Where does the real-world problem come from?",
    a: "For middle school cohorts, students may identify a challenge that matters to them or work from a real-world problem provided by a school, nonprofit, community organization, or business partner. For high school cohorts (Industry Challenge Edition), the challenge is supplied or sponsored by a business, nonprofit, school, government or community organization, or other participating industry partner.",
  },
  {
    q: "If our organization sponsors an Industry Challenge, do we have to adopt the student solution?",
    a: "No. Partners provide context, requirements, and feedback but are not obligated to implement any student solution. Partners may choose to continue developing or adopt a student solution, subject to the program's student/parent participation and intellectual-property terms. There is no obligation to adopt a solution.",
  },
  {
    q: "Are seed awards or internships guaranteed?",
    a: "No. Select sponsored cohorts may include seed awards or other recognition for standout student solutions. For high school cohorts, depending on the participating partner, recognition may include seed awards, mentorship, continued development opportunities, internship exposure, or other partner-supported opportunities. Internships and employment are never guaranteed.",
  },
  {
    q: "Can a company or community organization sponsor students?",
    a: "Yes. Companies, foundations, and community partners may sponsor individual student seats, a group of students, or a complete six-week cohort. Use the \"Bring the Lab to your school\" or \"Sponsor Student Seats\" pathway above to start a conversation.",
  },
  {
    q: "Where is the program held?",
    a: "The AI Build Lab is delivered in person at participating schools and approved community locations. Available cohort locations will be listed on this page when parent registration opens.",
  },
  {
    q: "What technology do students need?",
    a: "A laptop or Chromebook with internet access. All AI and no-code tools we use are browser-based and age-appropriate. We help you evaluate any tool against your district or family safety policies before Week 1.",
  },
  {
    q: "Is student AI use safe and responsible?",
    a: "Yes. Responsible AI use is baked into the curriculum from Week 1 — including how to evaluate AI output, protect personal information, cite AI assistance, and think critically about the tools. High school students go deeper into AI governance, privacy and security, bias, and intellectual-property considerations. Parents and administrators receive a plain-language overview before the program begins.",
  },
];

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 lg:py-32" data-testid="sc-faq" style={{ background: C.white }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Common questions</div>
            <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(30px, 4vw, 50px)", color: C.navy }}>
              Questions from parents, principals & partners.
            </h2>
            <p className="mt-6 text-[16px] leading-[1.65]" style={{ color: C.muted }}>
              Don&rsquo;t see your question? Reach out through the inquiry form below — Donja answers every message personally.
            </p>
            <a href="#inquiry" className="mt-6 inline-flex items-center gap-2 text-[14px] font-bold" style={{ color: C.royal }}>
              Ask a question <ArrowRight size={14} />
            </a>
          </div>
        </div>
        <div className="lg:col-span-8">
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = open === i;
              return (
                <div
                  key={i}
                  className="rounded-2xl border-2 overflow-hidden transition-all"
                  style={{
                    background: isOpen ? C.softNeutral : C.white,
                    borderColor: isOpen ? C.royal : "rgba(8,43,92,0.1)",
                  }}
                  data-testid={`sc-faq-item-${i}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between text-left gap-4 px-5 sm:px-6 py-5 sm:py-6"
                    data-testid={`sc-faq-toggle-${i}`}
                  >
                    <span className="font-bold text-[16px] sm:text-[17px] leading-snug" style={{ color: C.navy }}>{item.q}</span>
                    <ChevronDown
                      size={20}
                      className="flex-shrink-0 transition-transform"
                      style={{ color: isOpen ? C.royal : C.muted, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                    />
                  </button>
                  {isOpen && (
                    <div className="px-5 sm:px-6 pb-6 -mt-1 text-[15px] leading-[1.7]" style={{ color: C.text }}>
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section className="py-24 lg:py-32 relative overflow-hidden" style={{ background: C.navy }} data-testid="sc-final-cta">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl" style={{ background: C.royal }} />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl" style={{ background: C.orange }} />
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-6" style={{ color: C.orange }}>Bring it to your students</div>
        <h2 className="font-black text-white leading-[1.03] tracking-[-0.03em]" style={{ fontSize: "clamp(36px, 5.5vw, 68px)" }}>
          Bring the future of <span style={{ color: C.orange }}>innovation</span> to your students.
        </h2>
        <p className="mt-8 text-[19px] leading-[1.6] text-white/80 max-w-2xl mx-auto">
          Let&rsquo;s explore how the six-week AI Build Lab can support your school or organization&rsquo;s goals for student engagement, AI literacy, entrepreneurship, STEAM education, and career readiness.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <a href="#inquiry">
            <Button className="h-14 px-8 rounded-full text-white font-bold text-[15px] hover:opacity-90 shadow-2xl" style={{ background: C.orange, boxShadow: `0 15px 40px -10px ${C.orange}80` }} data-testid="sc-finalcta-primary">
              Schedule a School Conversation <ArrowRight size={17} className="ml-2" />
            </Button>
          </a>
          <a href="#inquiry">
            <Button variant="outline" className="h-14 px-7 rounded-full font-semibold text-[15px] bg-transparent text-white border-2 border-white/30 hover:bg-white/10" data-testid="sc-finalcta-secondary">
              Request Program Information
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------- INQUIRY FORM ---------- */
function InquiryForm({ inquiryType, setInquiryType }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", job_title: "",
    organization_name: "", org_type: "school",
    audience_served: "Middle and high school students",
    estimated_participants: "", preferred_timeframe: "",
    training_location: "in_person", main_objective: "", additional_comments: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim() || !form.organization_name.trim() || !form.main_objective.trim()) {
      toast.error("Please fill in your name, email, school or organization, and what you'd like to accomplish.");
      return;
    }
    setSubmitting(true);
    try {
      await submitOrgInquiry({
        ...form,
        interest_types: ["student_innovation_camp"],
        additional_comments: `[STUDENT CAMP INQUIRY · ${inquiryType.toUpperCase()}] ${form.additional_comments || ""}`.trim(),
      });
      setDone(true);
      toast.success("Inquiry received. Donja will follow up within 1–2 business days.");
    } catch (err) {
      toast.error(err?.message || "Something went wrong. Please email info@itsthefreedominme.com.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <section id="inquiry" className="py-24 lg:py-32" style={{ background: C.softNeutral }} data-testid="sc-inquiry-success">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-2xl grid place-items-center mx-auto mb-8 shadow-xl" style={{ background: C.orange }}>
            <CheckCircle2 size={38} className="text-white" strokeWidth={2.5} />
          </div>
          <h2 className="font-black leading-tight tracking-[-0.02em]" style={{ fontSize: "clamp(32px, 4.5vw, 52px)", color: C.navy }}>Thank you for reaching out.</h2>
          <p className="mt-5 text-[17px] leading-[1.65]" style={{ color: C.muted }}>
            Donja personally reads every inquiry. You&rsquo;ll hear back within 1–2 business days with next steps, sample dates, and a scoped proposal.
          </p>
          <Link to="/student-camp" className="mt-8 inline-block text-[14px] font-semibold hover:underline" style={{ color: C.royal }}>&larr; Back to the AI Build Lab</Link>
        </div>
      </section>
    );
  }

  return (
    <section id="inquiry" className="py-24 lg:py-32" style={{ background: C.softNeutral }} data-testid="sc-inquiry">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[11px] uppercase font-bold tracking-[0.22em] mb-4" style={{ color: C.orange }}>Schedule a Conversation</div>
          <h2 className="font-black leading-[1.02] tracking-[-0.03em]" style={{ fontSize: "clamp(32px, 4.5vw, 56px)", color: C.navy }}>
            Let&rsquo;s build the AI Build Lab for <span style={{ color: C.royal }}>your students.</span>
          </h2>
          <p className="mt-6 text-[17px] leading-[1.65]" style={{ color: C.muted }}>
            The program is <strong style={{ color: C.text }}>custom-scoped per school</strong> — we&rsquo;ll match your calendar, cohort size, budget, and student outcomes. Donja follows up personally with next steps and a proposal.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { icon: Compass, text: "Custom-scoped per school or organization" },
              { icon: Users, text: "15–30 students · solo or small teams" },
              { icon: Sparkles, text: "Community-invited Innovation Showcase in Week 6" },
              { icon: Trophy, text: "Portfolio-ready outcomes for every student" },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="flex items-center gap-3 text-[15px]" style={{ color: C.text }}>
                  <div className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0" style={{ background: C.lightBlue, color: C.royal }}>
                    <Icon size={17} />
                  </div>
                  {p.text}
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} className="rounded-3xl p-7 sm:p-9 space-y-5 shadow-xl" style={{ background: C.white, borderTop: `4px solid ${C.orange}` }} data-testid="sc-inquiry-form">
            <div>
              <Label className="text-[11px] uppercase font-bold tracking-[0.14em]" style={{ color: C.navy }}>I&rsquo;m reaching out as a *</Label>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { v: "school", l: "School / Principal" },
                  { v: "parent", l: "Parent" },
                  { v: "partner", l: "Community partner" },
                  { v: "sponsor", l: "Sponsor" },
                  { v: "mentor", l: "Mentor / Judge" },
                  { v: "general", l: "General inquiry" },
                ].map((o) => (
                  <button key={o.v} type="button" onClick={() => setInquiryType(o.v)} data-testid={`sc-inquiry-type-${o.v}`}
                    className="px-3 py-2.5 rounded-lg font-semibold text-[13px] border-2 transition-all text-left"
                    style={{
                      borderColor: inquiryType === o.v ? C.orange : "rgba(8,43,92,0.12)",
                      background: inquiryType === o.v ? "rgba(255,122,0,0.06)" : C.white,
                      color: inquiryType === o.v ? C.navy : C.muted,
                    }}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="First name *" v={form.first_name} on={(v) => upd("first_name", v)} tid="sc-f-first" />
              <F label="Last name" v={form.last_name} on={(v) => upd("last_name", v)} tid="sc-f-last" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Email *" type="email" v={form.email} on={(v) => upd("email", v)} tid="sc-f-email" />
              <F label="Phone (optional)" v={form.phone} on={(v) => upd("phone", v)} tid="sc-f-phone" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Your role" v={form.job_title} on={(v) => upd("job_title", v)} placeholder="Principal, STEM lead, sponsor…" tid="sc-f-role" />
              <F label="School / organization *" v={form.organization_name} on={(v) => upd("organization_name", v)} tid="sc-f-org" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Approx. # of students" v={form.estimated_participants} on={(v) => upd("estimated_participants", v)} placeholder="15–30 recommended" tid="sc-f-students" />
              <F label="Preferred timeframe" v={form.preferred_timeframe} on={(v) => upd("preferred_timeframe", v)} placeholder="Fall, spring, summer…" tid="sc-f-timeframe" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold tracking-[0.14em]" style={{ color: C.navy }}>What would success look like? *</Label>
              <Textarea rows={4} value={form.main_objective} onChange={(e) => upd("main_objective", e.target.value)}
                placeholder="Tell me about your students, your goals, and what a successful program would look like for your school or organization."
                className="mt-1.5 border-2 focus-visible:ring-0 focus-visible:border-blue-500" style={{ borderColor: "rgba(8,43,92,0.12)" }} data-testid="sc-f-objective" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-bold tracking-[0.14em]" style={{ color: C.navy }}>Anything else (optional)</Label>
              <Textarea rows={2} value={form.additional_comments} onChange={(e) => upd("additional_comments", e.target.value)}
                className="mt-1.5 border-2 focus-visible:ring-0 focus-visible:border-blue-500" style={{ borderColor: "rgba(8,43,92,0.12)" }} data-testid="sc-f-comments" />
            </div>
            <Button type="submit" disabled={submitting}
              className="w-full h-13 rounded-full text-white font-bold text-[15px] hover:opacity-90 shadow-lg"
              style={{ background: C.orange, boxShadow: `0 10px 25px -8px ${C.orange}90` }} data-testid="sc-f-submit">
              {submitting ? <Loader2 size={17} className="animate-spin" /> : <>Send my inquiry <Send size={15} className="ml-2" /></>}
            </Button>
            <p className="text-[11px] text-center" style={{ color: C.muted }}>Personal reply within 1–2 business days.</p>
          </form>
        </div>
      </div>
    </section>
  );
}

function F({ label, v, on, type = "text", placeholder, tid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase font-bold tracking-[0.14em]" style={{ color: C.navy }}>{label}</Label>
      <Input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="mt-1.5 border-2 focus-visible:ring-0 focus-visible:border-blue-500" style={{ borderColor: "rgba(8,43,92,0.12)" }} data-testid={tid} />
    </div>
  );
}

/* ---------- FOOTER ---------- */
function Foot() {
  return (
    <footer className="py-12 border-t" style={{ background: C.navy, borderColor: "rgba(255,255,255,0.08)" }} data-testid="sc-footer">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg grid place-items-center text-white shadow-md" style={{ background: `linear-gradient(135deg, ${C.royal}, ${C.orange})` }}>
            <span className="font-black text-[13px]">AI</span>
          </div>
          <div>
            <div className="text-white font-black text-[13px] tracking-tight leading-none">AI BUILD LAB · Student Innovation</div>
            <div className="text-white/60 text-[11px] leading-none mt-1">Freedom In Me, LLC · &copy; {new Date().getFullYear()}</div>
          </div>
        </div>
        <div className="flex items-center gap-5 text-[13px] text-white/70">
          <a href="mailto:info@itsthefreedominme.com" className="hover:text-white">info@itsthefreedominme.com</a>
          <Link to="/" className="hover:text-white">Adult AI Build Lab &rarr;</Link>
        </div>
      </div>
    </footer>
  );
}
